import { useCallback } from "react";

import { trackEvent } from "utility/mixpanel";
import { getTrackingParamsFromUrl } from "../components/discover/utils";

import { CardItemType } from "components/ui/ContentCard";

export interface UseCardTrackingProps {
  item: CardItemType | null;
  listName?: string;
  listPosition?: number;
  showBiometrics?: boolean;
  tabContext?: string;
}

enum ContentEventType {
  LearnerRecording = "Recording",
  LearnerArticle = "Article",
  LearnerGroupSession = "Live Session",
  LearnerPrivateSession = "Private Session",
  LearnerBiometricsGroupSession = "Heart Rate Session",
  LearnerBiometricsPrivateSession = "Private Heart Rate Session",
}

const getEventType = (type: string, showBiometrics: boolean) => {
  switch (type) {
    case "LearnerRecording":
      return ContentEventType.LearnerRecording;
    case "LearnerArticle":
      return ContentEventType.LearnerArticle;
    case "LearnerGroupSession":
      return showBiometrics
        ? ContentEventType.LearnerBiometricsGroupSession
        : ContentEventType.LearnerGroupSession;
    case "LearnerPrivateSession":
      return showBiometrics
        ? ContentEventType.LearnerBiometricsPrivateSession
        : ContentEventType.LearnerPrivateSession;
    default:
      return "";
  }
};

export interface SimpleTrackingOptions {
  mixpanelEvent: string;
  eventData?: Record<string, any>;
}

export const useCardTracking = ({
  item,
  listName,
  listPosition,
  showBiometrics = false,
  tabContext,
}: UseCardTrackingProps) => {
  const eventType = item ? getEventType(item.__typename, showBiometrics) : "";

  const createTrackingFunction = useCallback(
    (mixpanelEvent: string) => {
      return () => {
        if (!item) return;
        const { appliedFilters, searchTerm } = getTrackingParamsFromUrl();
        const eventData: Record<string, any> = {
          [`${eventType} ID`]: item.id,
          [`${eventType} Title`]: item.title,
          "List Name": listName,
          "List Position": listPosition,
          "Current Page": window.location.href,
          "Applied Filters": appliedFilters,
          "Search Term": searchTerm,
        };

        // Add tab context if available (for discover page)
        if (tabContext) {
          eventData["Tab Context"] = tabContext;
        }

        trackEvent(mixpanelEvent, eventData);
      };
    },
    [item, listName, listPosition, eventType, tabContext]
  );

  const logImpression = useCallback(
    () => createTrackingFunction(`${eventType} Card Impression`)(),
    [createTrackingFunction, eventType]
  );

  const logClick = useCallback(
    () => createTrackingFunction(`${eventType} Card Clicked`)(),
    [createTrackingFunction, eventType]
  );

  const logPlayClick = useCallback(
    () => createTrackingFunction(`${eventType} Play Button Clicked`)(),
    [createTrackingFunction, eventType]
  );

  const logFavoriteClick = useCallback(
    (isFavourite: boolean) => {
      if (!item) return;
      const action = isFavourite
        ? "Removed from favourites"
        : "Added to Favourites";
      createTrackingFunction(`${eventType} ${action}`)();
    },
    [item, eventType, createTrackingFunction]
  );

  const logRegisterClick = useCallback(
    () => createTrackingFunction(`${eventType} Register Button Clicked`)(),
    [createTrackingFunction, eventType]
  );

  return {
    logImpression,
    logClick,
    logPlayClick,
    logFavoriteClick,
    logRegisterClick,
  };
};
